import React from 'react'
import { motion } from 'framer-motion'
import { useTheme } from '../context/ThemeContext'

interface ButtonProps {
  children: React.ReactNode
  variant?: 'primary' | 'secondary' | 'gradient' | 'outline'
  className?: string
  icon?: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  className = '',
  icon,
  onClick,
  disabled = false,
  size = 'medium',
}) => {
  const { theme } = useTheme();

  // Base styles with enhanced focus states
  const baseStyles =
    'rounded-md font-semibold transition-all duration-300 flex items-center justify-center gap-2 group relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-opacity-50'
  
  // Variant styles based on current theme with enhanced light theme colors
  const variantStyles = {
    primary: theme === 'dark' 
      ? 'bg-[#4B7BFF] text-white hover:bg-[#3C66D1] focus:ring-[#4B7BFF]'
      : 'bg-[#4B7BFF] text-white hover:bg-[#3C66D1] focus:ring-[#4B7BFF]/50 shadow-md shadow-blue-300/20',
    secondary: theme === 'dark'
      ? 'bg-[#8E44AD] text-white hover:bg-[#7A3798] focus:ring-[#8E44AD]'
      : 'bg-[#8E44AD] text-white hover:bg-[#7A3798] focus:ring-[#8E44AD]/50 shadow-md shadow-purple-300/20',
    gradient: theme === 'dark'
      ? 'bg-gradient-to-r from-[#4B7BFF] to-[#8E44AD] text-white hover:opacity-90 focus:ring-[#6C5CE7]'
      : 'bg-gradient-to-r from-[#4B7BFF] to-[#8E44AD] text-white hover:opacity-90 focus:ring-[#6C5CE7]/50 shadow-md shadow-purple-300/20',
    outline: theme === 'dark'
      ? 'bg-transparent border border-white/30 text-white hover:bg-white/10 focus:ring-white/50'
      : 'bg-transparent border border-[#4B7BFF] text-[#4B7BFF] hover:bg-[#4B7BFF]/5 focus:ring-[#4B7BFF]/50',
  }

  // Refined sizing with better vertical alignment
  const sizeStyles = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-5 py-2.5 text-base',
    large: 'px-7 py-3.5 text-lg',
  }

  return (
    <motion.button
      className={[
        baseStyles,
        variantStyles[variant] || variantStyles.primary,
        sizeStyles[size] || sizeStyles.medium,
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md',
        className,
      ].join(' ')}
      whileHover={!disabled ? { scale: 1.03 } : undefined}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
    >
      {/* Enhanced overlay effect for light theme */}
      <span className={`absolute inset-0 ${theme === 'dark' ? 'bg-white/10' : 'bg-white/20'} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
      {icon && <span className="w-5 h-5">{icon}</span>}
      <span>{children}</span>
    </motion.button>
  )
}

export default Button
